import { Formik } from 'formik'
import { useState } from 'react'
import { Form } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import * as Yup from 'yup'

import { InlineTable } from 'src/feature/bite/component'
import { useStore } from 'src/feature/store/hook'
import { useUpdateStoreContact } from '../../context/useMutation'
import { useGetStoreContactTitleList } from '../../context/useQuery'

const StoreContact = ({ loading }: { loading: boolean }) => {
  useGetStoreContactTitleList()

  const intl = useIntl()
  const { detail, detailSet, storeContactTitleList } = useStore()
  const [editable, setEditable] = useState(false)
  const { mutateAsync, isLoading } = useUpdateStoreContact({ storeId: detail?.id || '' })

  const Data = detail?.Contact ? detail?.Contact : null

  const selects = storeContactTitleList?.map((item) => {
    return {
      value: item.id,
      text: item.name,
    }
  })

  const column = [
    [
      {
        key: 'FirstName',
        dataIndex: 'firstname',
        title: intl.formatMessage({ id: 'STORE.DETAIL.CONTACT.NAME' }),
        edit: 'input',
      },
      {
        key: 'LastName',
        dataIndex: 'lastname',
        title: intl.formatMessage({ id: 'STORE.DETAIL.CONTACT.SURNAME' }),
        edit: 'input',
      },
    ],
    [
      {
        key: 'Phone',
        dataIndex: 'phone',
        formName: 'phone',
        title: intl.formatMessage({ id: 'STORE.DETAIL.CONTACT.PHONE' }),
        edit: 'phone',
      },
      {
        key: 'Email',
        dataIndex: 'email',
        title: intl.formatMessage({ id: 'STORE.DETAIL.CONTACT.EMAIL' }),
        edit: 'input',
        maxLength: 101,
      },
      {
        key: 'titleID',
        dataIndex: 'titleName',
        formName: 'titleID',
        title: intl.formatMessage({ id: 'STORE.DETAIL.CONTACT.TITLE' }),
        edit: 'select',
        selects: selects,
      },
    ],
  ]

  const validationSchema = Yup.object().shape({
    firstname: Yup.string()
      .required(intl.formatMessage({ id: 'GLOBAL.VALIDATION.REQUIRED_FIELD' }))
      .max(50, intl.formatMessage({ id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD' }, { length: 50 })),
    lastname: Yup.string()
      .required(intl.formatMessage({ id: 'GLOBAL.VALIDATION.REQUIRED_FIELD' }))
      .max(50, intl.formatMessage({ id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD' }, { length: 50 })),
    phone: Yup.string()
      .required(intl.formatMessage({ id: 'GLOBAL.VALIDATION.REQUIRED_FIELD' }))
      // +1 (999) 999-9999
      .matches(/^\+1 \(\d{3}\) \d{3}-\d{4}$/, intl.formatMessage({id: 'GLOBAL.VALIDATION.PHONE'})),
    email: Yup.string()
      .email(intl.formatMessage({ id: 'GLOBAL.VALIDATION.EMAIL' }))
      .required(intl.formatMessage({ id: 'GLOBAL.VALIDATION.REQUIRED_FIELD' }))
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, intl.formatMessage({ id: 'GLOBAL.VALIDATION.EMAIL' }))
      .max(100, intl.formatMessage({ id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD' }, { length: 100 })),
    // titleId: Yup.string().required('Required'),
    titleID: Yup.string().nullable(),
  })

  const handleSave = async (values: {
    firstname: string
    lastname: string
    phone: string
    email: string
    titleID: string
  }) => {
    let newPhone = values.phone.replace(/\+1|\(|\)|-|\s/g, '')

    const input = {
      titleID: values.titleID,
      firstName: values.firstname,
      lastName: values.lastname,
      email: values.email,
      phone: newPhone,
      CountryPhoneCode: '+1',
      middleName: '',
      gender: 1,
    }

    const responseDetailUpdate = await mutateAsync({ input })

    if (detail && detail.Contact) {
      detailSet({
        ...detail,
        Contact:
          responseDetailUpdate && responseDetailUpdate.detail
            ? responseDetailUpdate.detail
            : {
              ...detail?.Contact,
              ...input,
            },
      })
    }

    setEditable(false)
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header'>
        <div className='card-title m-0'>
          <div>
            <h3 className='fw-bold mb-2 mt-5'>
              {intl.formatMessage({ id: 'STORE.DETAIL.CONTACT' })}
            </h3>
            <p className='text-muted fw-normal fs-6'>
              {intl.formatMessage({ id: 'STORE.DETAIL.CONTACT_TOOLTIP' })}
            </p>
          </div>
        </div>
      </div>

      {Data && (
        <Formik
          initialValues={{
            firstname: Data?.firstname || '',
            lastname: Data?.lastname || '',
            phone: `+1 (${Data?.phone?.slice(0, 3)}) ${Data?.phone?.slice(3, 6)}-${Data?.phone?.slice(6)}` || '',
            email: Data?.email || '',
            titleID: Data?.titleID || '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleSave(values)
          }}
        >
          {({ values, errors, touched, setTouched, handleSubmit, resetForm }) => {
            console.log(values)
            return (
              <Form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleSubmit(e)
                }}
              >
                <div className='card-body p-9'>
                  <InlineTable
                    loading={loading}
                    dataSource={Data}
                    columns={column}
                    editable={editable}
                    values={values}
                  />
                </div>
                <div className='card-footer d-flex w-100 justify-content-end'>
                  {editable ? (
                    <div>
                      <button
                        className='btn btn-primary'
                        style={{ marginRight: 10 }}
                        onClick={() => {
                          setEditable(false)
                          resetForm()
                        }}
                      >
                        <i className='bi bi-x me-2'></i>
                        {intl.formatMessage({ id: 'GLOBAL.BUTTON.CANCEL' })}
                      </button>
                      <button className='btn btn-primary' disabled={isLoading}>
                        <i className='bi bi-save2 me-2'></i>
                        {isLoading
                          ? intl.formatMessage({ id: 'GLOBAL.BUTTON.SAVING' })
                          : intl.formatMessage({ id: 'GLOBAL.BUTTON.SAVE' })}
                      </button>
                    </div>
                  ) : (
                    <button className='btn btn-primary' onClick={() => setEditable(true)}>
                      <i className='bi bi-pencil me-2'></i>
                      {intl.formatMessage({ id: 'GLOBAL.BUTTON.EDIT' })}
                    </button>
                  )}
                </div>
              </Form>
            )
          }}
        </Formik>
      )}
    </div>
  )
}

export default StoreContact
